// ADR 7.10.26ad §1 — the launch that waited on the network.
//
// The bootstrap chain (refresh → `/features`) can neither succeed nor fail on
// lie-fi until its 20 s ceiling, and the snapshot it would fall back to was
// only consulted once it *failed*. So the chain is raced against a 500 ms
// deadline: past it, a snapshot paints the session it last confirmed and the
// chain keeps running. The chain is never aborted — the refresh is
// single-flight and every authed request that follows joins it.
//
// The late chain's own session is never applied: each of its outcomes already
// lands through an existing listener (the refreshed-identity patch, the
// signed-out event, `useOfflineResume`'s re-read on the next transition to
// online). The continuation has one active case, a refresh for another user.
//
// The three primitives take their effects and event source as arguments, so
// `bootDeadline.test.ts` drives them in `node` with fake timers and a bare
// `EventTarget`; `bootWithDeadline` is the one place they meet the real ones.

import type { AuthSession } from './session'
import { SESSION_REFRESHED_EVENT, signOutLocally, type SessionIdentity } from './tokenStore'
import { clearAllTutorSessions } from '../features/tutor/sessions/store'
import { resumedSession } from '../offline/offlineResume'
import { reportUnreachable } from '../offline/onlineSignal'
import { BOOT_RESUME_DEADLINE_MS } from '../offline/requestDeadline'

export type BootOutcome = { session: AuthSession; resumed: boolean }

export type BootRace = { kind: 'settled'; outcome: BootOutcome } | { kind: 'resumed'; session: AuthSession }

/** The identities `SESSION_REFRESHED_EVENT` reports, from the chain's start. */
export interface RefreshedIdentities {
  latest(): SessionIdentity | null
  onNext(listener: (identity: SessionIdentity) => void): () => void // returns unsubscribe
  stop(): void
}

/**
 * Started with the chain, because the provider's `patchIdentity` ignores a
 * session that is still `loading`: without it, an identity the refresh
 * reported before the deadline would be lost to the resume.
 */
export function watchRefreshedIdentities(target: EventTarget): RefreshedIdentities {
  let latest: SessionIdentity | null = null
  const listeners = new Set<(identity: SessionIdentity) => void>()
  const onRefreshed = (event: Event) => {
    const identity = (event as CustomEvent<SessionIdentity>).detail
    latest = identity
    for (const listener of [...listeners]) listener(identity)
  }
  target.addEventListener(SESSION_REFRESHED_EVENT, onRefreshed)
  return {
    latest: () => latest,
    onNext(listener) {
      listeners.add(listener)
      return () => void listeners.delete(listener)
    },
    stop() {
      target.removeEventListener(SESSION_REFRESHED_EVENT, onRefreshed)
      listeners.clear()
    },
  }
}

/**
 * The first of: the chain settling (today's outcome, unchanged), or the
 * deadline passing with a snapshot to resume from. A deadline with no snapshot
 * settles nothing, and the race waits for the chain. The snapshot is read only
 * once the deadline fires, and a chain finishing during that read still wins.
 */
export function raceBootDeadline(
  chain: Promise<BootOutcome>,
  fallback: () => Promise<AuthSession | null>, // resumedSession(identity known at the deadline); the continuation re-checks
  deadlineMs: number,
  signal: AbortSignal, // clears the deadline timer on effect cleanup
): Promise<BootRace> {
  return new Promise<BootRace>((resolve, reject) => {
    let done = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const finish = (settle: () => void) => {
      if (done) return
      done = true
      clearTimeout(timer)
      settle()
    }
    const onDeadline = () => {
      // A failed snapshot read is no snapshot: keep waiting for the chain.
      fallback().then((session) => {
        if (session !== null) finish(() => resolve({ kind: 'resumed', session }))
      }, () => undefined)
    }
    if (!signal.aborted) timer = setTimeout(onDeadline, deadlineMs)
    signal.addEventListener('abort', () => clearTimeout(timer), { once: true })
    chain.then(
      (outcome) => finish(() => resolve({ kind: 'settled', outcome })),
      (error: unknown) => finish(() => reject(error)),
    )
  })
}

export interface ContinuationDeps {
  clearAll: () => void | Promise<void>
  signOutLocally: () => void
  patchIdentity: (identity: SessionIdentity) => void // the provider's, for a pre-subscribe same-user event
  live: () => boolean // the effect's existing `live` flag
}

/**
 * After a resume, until the chain settles: a refresh for a user other than the
 * one resumed wipes the whole tutor store (`patchIdentity` has already moved
 * `userId`, so `sessionTransition` would name the wrong account) and signs out,
 * at once. Everything the chain itself settles is ignored.
 *
 * It first re-reads `latest()`, closing the window between the deadline's read
 * and the subscription, where `patchIdentity` still ignored a `loading` session.
 */
export async function continueAfterResume(
  chain: Promise<BootOutcome>,
  resumedUserId: string,
  identities: RefreshedIdentities,
  deps: ContinuationDeps,
): Promise<void> {
  let acted = false
  const judge = (identity: SessionIdentity) => {
    if (acted || !deps.live() || identity.userId === resumedUserId) return
    acted = true
    void deps.clearAll()
    deps.signOutLocally()
  }
  const early = identities.latest()
  if (early !== null && early.userId === resumedUserId) deps.patchIdentity(early)
  else if (early !== null) judge(early)
  const unsubscribe = identities.onNext(judge)
  try {
    await chain.catch(() => undefined)
  } finally {
    unsubscribe()
    identities.stop()
  }
}

/** What `useBootstrap` hands over: the provider's setters and the effect's `live` flag. */
export interface BootCallbacks {
  apply: (next: AuthSession) => void
  markResumed: () => void
  patchIdentity: (identity: SessionIdentity) => void
  live: () => boolean
}

/**
 * The bootstrap, raced against {@link BOOT_RESUME_DEADLINE_MS}. `start` is
 * `bootstrapSession`, passed in so this module does not import `session.tsx`
 * back. Every step after an `await` checks `live`, and an aborted `signal`
 * clears the timer and stops the watcher, so StrictMode's double run cannot
 * settle twice.
 */
export async function bootWithDeadline(
  start: () => Promise<BootOutcome>,
  boot: BootCallbacks,
  signal: AbortSignal,
): Promise<void> {
  const identities = watchRefreshedIdentities(window)
  signal.addEventListener('abort', () => identities.stop(), { once: true })
  const chain = start()
  const fallback = () => resumedSession(identities.latest())
  const race = await raceBootDeadline(chain, fallback, BOOT_RESUME_DEADLINE_MS, signal)
  if (!boot.live()) return
  if (race.kind === 'settled') {
    identities.stop()
    boot.apply(race.outcome.session)
    if (race.outcome.resumed) boot.markResumed()
    return
  }
  boot.apply(race.session)
  boot.markResumed()
  // §2 — the network is unconfirmed: queries pause on cached data, the probe starts.
  reportUnreachable('boot_deadline')
  await continueAfterResume(chain, race.session.userId ?? '', identities, {
    clearAll: clearAllTutorSessions,
    signOutLocally,
    patchIdentity: boot.patchIdentity,
    live: boot.live,
  })
}
